import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface Props extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> { to: string; children: ReactNode }

/** Scrolls smoothly when the target is on the current page, and routes to the right page when it isn't. */
export function SmartLink({ to, children, onClick, ...rest }: Props) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [rawPath, hash] = to.split('#')
  const path = rawPath || '/'

  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    if (pathname === path) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth'
      if (hash) document.getElementById(hash)?.scrollIntoView({ behavior, block: 'start' })
      else window.scrollTo({ top: 0, behavior })
      window.history.replaceState(window.history.state, '', hash ? `${path}#${hash}` : path)
    } else {
      navigate(hash ? `${path}#${hash}` : path)
    }
  }
  return <a href={to} onClick={handle} {...rest}>{children}</a>
}
